import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { LoginForm, MagicLinkSent } from '../components/auth';
import { useAuth } from '../contexts/AuthContext';
import './login-page.css';

const LoginPage: React.FC = () => {
  const [magicLinkSent, setMagicLinkSent] = useState(false);
  const [loginData, setLoginData] = useState<{ email: string } | null>(null);
  
  const { login, isAuthenticated, isLoading, checkAuthStatus, hasCheckedAuth, handleAuthenticationSuccess } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const from = location.state?.from?.pathname || '/group-selection';

  // Force auth check if we haven't done it yet (e.g., coming from homepage)
  useEffect(() => {
    if (!hasCheckedAuth && !isLoading) {
      checkAuthStatus();
    }
  }, [hasCheckedAuth, isLoading, checkAuthStatus]);

  // Redirect authenticated users to dashboard (but not if they just sent a magic link)
  useEffect(() => {
    if (isAuthenticated && !isLoading && !magicLinkSent) {
      navigate(from, { replace: true });
    }
  }, [isAuthenticated, isLoading, magicLinkSent, navigate, from, hasCheckedAuth]);

  // Remove main padding for this page
  useEffect(() => {
    document.body.classList.add('homepage-route');
    return () => {
      document.body.classList.remove('homepage-route');
    };
  }, []);

  const handleLogin = async (data: { email: string }) => {
    await login(data.email);
    setLoginData(data);
    setMagicLinkSent(true);
  };

  const handleBackToLogin = () => {
    setMagicLinkSent(false);
    setLoginData(null);
  };

  const handleSocialLoginSuccess = async (result: any) => {
    // Transform groups to the format expected by AuthContext
    const transformedGroups = result.groups.map((g: any) => ({
      id: g.group.id,
      slug: g.group.slug,
      name: g.group.name,
      isAdmin: g.isAdmin,
      memberCount: g.personCount,
      createdAt: g.group.createdAt,
    }));
    
    // Update AuthContext with user and groups
    const authData = {
      user: result.user,
      groups: transformedGroups,
    };
    
    await handleAuthenticationSuccess(authData);
    
    // Always show group selection if user has multiple groups
    if (transformedGroups.length === 1) {
      navigate(`/${transformedGroups[0].slug}/dashboard`, { replace: true });
    } else {
      navigate('/group-selection', { replace: true });
    }
  };

  // Show loading while checking authentication
  if (isLoading) {
    return (
      <div className="login-page">
        <header className="login-page-header">
          <nav className="login-nav">
            <Link to="/" className="login-logo">
              CosaVorrei
            </Link>
          </nav>
        </header>
        <div className="login-main">
          <div className="login-container">
          </div>
        </div>
      </div>
    );
  }

  if (magicLinkSent && loginData) {
    return (
      <div className="login-page">
        <header className="login-page-header">
          <nav className="login-nav">
            <Link to="/" className="login-logo">
              CosaVorrei
            </Link>
          </nav>
        </header>
        <div className="login-main">
          <MagicLinkSent
            email={loginData.email}
            onBackToLogin={handleBackToLogin}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="login-page">
      <header className="login-page-header">
        <nav className="login-nav">
          <Link to="/" className="login-logo">
            CosaVorrei
          </Link>
          <div className="login-nav-buttons">
            <Link to="/create-group" className="login-nav-btn primary">
              Crea Gruppo
            </Link>
          </div>
        </nav>
      </header>
      
      <div className="login-main">
        <LoginForm 
          onSubmit={handleLogin} 
          onSocialLoginSuccess={handleSocialLoginSuccess}
        />
      </div>
    </div>
  );
};

export default LoginPage;